import type { FC } from 'react'
import { useMemo, useRef } from 'react'
import {
  Desktop,
  MediaContextProvider,
  TabletOrMobile
} from '@/components/atoms/media'
import { Box } from '@/components/atoms/Grid'
import { MainSlideMenuActions } from '@/components/organisms/mainSideSlideMenu'
import { useMainNavigation } from '@/providers/mainNavigationMenuProvider'
import { HeaderNotice, Market } from '@/network/graphql.g'
import styled from 'styled-components'
import { Asset, useAsset } from '@/providers/assetsProvider'
import { usePremiumManufacturersNavigation } from '@/providers/premiumManufacturersMenuProvider'
import { FocusMode, useCurrentPage } from '@/providers/focusModeProvider'
import { ContentWrapper } from '@/components/organisms/container'
import { ContentPaddings } from '@/components/organisms/pageContentWrapper'
import DesktopCategoryMenuRedesignWrapper from '@/components/organisms/desktopCategoryMenuRedesign'
import ToolbarRedesign from '@/components/organisms/toolbar/toolbarRedesign'
import MainSlideMenuRedesign from '@/components/organisms/mainSideSlideMenu/mainSlideMenuRedesign'
import ProductSearchAi from '@/components/molecules/aiSearch/productSearchAi'

type HeaderRedesignProps = {
  markets: Market[]
  showHeaderPromo: boolean
  headerNotice: HeaderNotice
  showSlideMenu: boolean
  setShowSlideMenu: React.Dispatch<React.SetStateAction<boolean>>
} & Rest

const HeaderRedesign: FC<HeaderRedesignProps> = ({
  markets,
  showHeaderPromo,
  headerNotice,
  showSlideMenu,
  setShowSlideMenu,
  ...rest
}) => {
  const mainNavigation = useMainNavigation()
  const premiumManufacturersMainNavigation = usePremiumManufacturersNavigation()

  const mainSlideMenuMenuRef = useRef<MainSlideMenuActions>(null)

  const otherMenuItems = useMemo(
    () =>
      mainNavigation?.children.filter(({ isMainCategory }) => !isMainCategory),
    [mainNavigation]
  )

  const navigationItems = useMemo(
    () =>
      mainNavigation?.children.filter(({ isMainCategory }) => isMainCategory),
    [mainNavigation]
  )
  const { getFocusType } = useCurrentPage()
  const isFocus = getFocusType() === FocusMode.Full
  const { asset, isCartProcessStep } = useAsset()
  const isSearchbarOpen = !isCartProcessStep

  return (
    <Box
      {...rest}
      showHeaderPromo={showHeaderPromo}
      headerNotice={headerNotice}
    >
      <MediaContextProvider>
        <Desktop>
          <ToolbarWrapper toolbarBackground={asset(Asset.toolbarBackground)}>
            <ContentWrapper
              px={{
                wide: ContentPaddings.wide,
                desktop: ContentPaddings.desktop
              }}
              suppressHydrationWarning
            >
              {!isFocus && (
                <DesktopCategoryMenuRedesignWrapper
                  navigationItems={navigationItems}
                  otherMenuItems={otherMenuItems}
                />
              )}
              <SearchWrapper>
                {isSearchbarOpen && (
                  <SearchWrapper>
                    <ProductSearchAi isCompact expandToLeft />
                  </SearchWrapper>
                )}
              </SearchWrapper>
            </ContentWrapper>
          </ToolbarWrapper>
        </Desktop>
        <TabletOrMobile>
          <ContentWrapper
            px={{
              tablet: ContentPaddings.tablet
            }}
            suppressHydrationWarning
          >
            <ToolbarRedesign
              data-testid="headerTabletOrMobile"
              onDrawerClick={() => mainSlideMenuMenuRef?.current.open()}
              showHeaderPromo={showHeaderPromo}
              headerNotice={headerNotice}
            />
            <MainSlideMenuRedesign
              ref={mainSlideMenuMenuRef}
              navigationItems={navigationItems}
              otherMenuItems={otherMenuItems}
              markets={markets}
              premiumManufacturers={premiumManufacturersMainNavigation}
              showSlideMenu={showSlideMenu}
              setShowSlideMenu={setShowSlideMenu}
              showHeaderPromo={showHeaderPromo}
              headerNotice={headerNotice}
            />
          </ContentWrapper>
        </TabletOrMobile>
      </MediaContextProvider>
    </Box>
  )
}

export default HeaderRedesign

const ToolbarWrapper = styled(Box)`
  background-color: ${(props) => props.theme.colors.towerColorWhite};
  width: 100%;
`

const SearchWrapper = styled(Box)`
  max-width: 352px;
  margin-left: auto;
  margin-bottom: 3px;
`
